import memoizeOne from 'memoize-one';
import React, { Component } from 'react';
import * as KeyCode from '~/libs/KeyCode';
import Input from '~/UI/Input/Input';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';

import AutocompletePopover from './Popover/AutocompletePopover';
import styles from './styles.less';

export interface Option {
	id: string;
	title: string;
}
interface Props {
	name: string | null;
	value?: string;
	isInvalid: boolean;
	multiple: boolean;
	hideOnSelect?: boolean;
	options: Option[];
	selectedOptions: Option[];
	styles?: {
		container: string;
		option: string;
		input: string;
		invalidInput: string;
	};
	inputStyles?: { default?: string; invalid?: string; input?: string; invalidInput?: string };
	onChange?(value: string): void;
	onSelect?(selectedOptions: Option[]): void;
	onFetch(): void;
}
interface State {
	expand: boolean;
	selectedIndex: number;
	autoscroll: boolean;
}

const selectSelectedOptionIds = memoizeOne((selectedOptions: Option[]) => {
	return new Set(selectedOptions.map((option) => option.id));
});

export default class Autocomplete extends Component<Props, State> {
	static defaultProps = {
		name: null,
		isInvalid: false,
		hideOnSelect: false,
		multiple: false,
		selectedOptions: [],
		styles,
		onFetch: () => {},
	};

	state = {
		expand: false,
		selectedIndex: 0,
		autoscroll: false,
	};

	autocompletePopoverHasFocus = false;

	inputRef: { current: Input | null } = React.createRef();

	componentDidMount() {
		this.props.onFetch();
	}

	componentDidUpdate(prevProps: Props) {
		const { options } = this.props;
		if (options !== prevProps.options) {
			this.setState((prevState) => ({
				selectedIndex: Math.max(0, Math.min(prevState.selectedIndex, options.length - 1)),
			}));
		}
	}

	renderInput() {
		const { name, value, isInvalid, multiple, styles, inputStyles } = this.props;

		return (
			<Input
				autoComplete='off'
				isInvalid={isInvalid}
				name={name || undefined}
				ref={this.inputRef}
				styles={multiple ? { default: styles?.input || '', invalid: styles?.invalidInput || '' } : inputStyles}
				value={value}
				onBlur={this.handleInputBlur}
				onChange={this.handleChange}
				onFocus={this.handleInputFocus}
				onKeyDown={this.handleKeyDown}
			/>
		);
	}

	select(option: Option) {
		const { selectedOptions, onSelect } = this.props;
		const selectedOptionIds = selectSelectedOptionIds(selectedOptions);
		if (selectedOptionIds.has(option.id)) {
			onSelect?.(selectedOptions.filter((selectedOption) => selectedOption.id !== option.id));
		} else {
			onSelect?.([...selectedOptions, option]);
		}
	}

	currentOption(): Option | null {
		const { value, options } = this.props;
		return options.find((option) => option.title === value) ?? null;
	}

	showPopover() {
		if (!this.state.expand) {
			this.setState({ expand: true });
		}
	}

	hidePopover() {
		if (this.state.expand) {
			this.setState({ expand: false });
		}
	}

	handleChange = (event: React.FormEvent<HTMLInputElement>) => {
		this.showPopover();
		this.props.onChange?.(event.currentTarget.value);
	};

	handleInputFocus = () => {
		this.showPopover();
	};

	handleInputBlur = (event: React.FormEvent<HTMLInputElement>) => {
		if (this.autocompletePopoverHasFocus) {
			return;
		}

		this.hidePopover();
		const option = this.currentOption();
		if (option !== null) {
			event.currentTarget.value = '';
			this.select(option);
		}
	};

	handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const {
			state: { selectedIndex },
			props: { options },
		} = this;

		switch (event.keyCode) {
			case KeyCode.UP:
				event.preventDefault();
				this.setState({
					expand: true,
					selectedIndex: Math.max(selectedIndex - 1, 0),
					autoscroll: true,
				});
				break;

			case KeyCode.DOWN:
				event.preventDefault();
				this.setState({
					expand: true,
					selectedIndex: Math.min(selectedIndex + 1, options.length - 1),
					autoscroll: true,
				});
				break;

			case KeyCode.ENTER:
				if (this.state.expand && options.length > 0) {
					event.preventDefault();
					this.hidePopover();
					this.select(options[selectedIndex]);
				}
				break;

			case KeyCode.PAGE_UP:
				event.preventDefault();
				this.setState({ expand: true, selectedIndex: 0, autoscroll: true });
				break;

			case KeyCode.PAGE_DOWN:
				event.preventDefault();
				this.setState({
					expand: true,
					selectedIndex: options.length - 1,
					autoscroll: true,
				});
				break;

			case KeyCode.ESCAPE:
				event.preventDefault();
				this.hidePopover();
				break;

			case KeyCode.BACKSPACE: {
				const { selectedOptions, onSelect } = this.props;
				if (event.currentTarget.value === '' && selectedOptions.length > 0) {
					onSelect?.(selectedOptions.slice(0, -1));
				}
				break;
			}

			default:
			// intentionally blank
		}
	};

	handleSelect = (index: number) => {
		this.setState({ selectedIndex: index, autoscroll: false });
	};

	handleOptionClick = (index: number) => {
		const { multiple, options } = this.props;

		this.setState({ selectedIndex: index, expand: multiple });
		this.select(options[index]);
		setTimeout(() => {
			this.inputRef.current?.focus();
			if (this.props.hideOnSelect) {
				this.hidePopover();
			}
		}, 0);
	};

	optionUnselected = (index: number) => {
		const { options, selectedOptions, onSelect } = this.props;
		onSelect?.(selectedOptions.filter((option) => option.id !== options[index].id));
	};

	render() {
		const {
			props: { multiple, options, selectedOptions, styles },
			state: { expand, selectedIndex, autoscroll },
		} = this;

		const input = multiple ? (
			<div className={styles?.container} onClick={() => this.inputRef.current?.focus()}>
				{selectedOptions.map((option) => (
					<div className={styles?.option} key={option.id} onClick={() => this.select(option)}>
						{option.title}
					</div>
				))}

				{this.renderInput()}
			</div>
		) : (
			this.renderInput()
		);

		return (
			<LayoutConstraint reference={input}>
				{expand ? (
					<AutocompletePopover
						autoscroll={autoscroll}
						options={options}
						selectedIndex={selectedIndex}
						selectedOptionIds={selectSelectedOptionIds(selectedOptions)}
						onBlur={() => (this.autocompletePopoverHasFocus = false)}
						onFocus={() => (this.autocompletePopoverHasFocus = true)}
						onOptionClick={this.handleOptionClick}
						onSelect={this.handleSelect}
					/>
				) : null}
			</LayoutConstraint>
		);
	}
}
